import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Sparkles,
  Clock,
  TrendingUp,
  Shield,
  Zap,
  ChevronRight,
  Star,
  Upload,
  Image,
  FileCheck,
} from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background overflow-hidden relative">
      {/* Skip to main content link for accessibility */}
      <a
        href="#hero-heading"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[200] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
      >
        Skip to main content
      </a>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center shadow-sm">
              <Sparkles className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="text-lg font-semibold text-foreground tracking-tight">
              RenovationAI
            </span>
          </div>

          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-muted-foreground hover:text-accent transition-colors text-sm font-medium">
              Features
            </a>
            <a href="#how-it-works" className="text-muted-foreground hover:text-accent transition-colors text-sm font-medium">
              How It Works
            </a>
            <a href="#pricing" className="text-muted-foreground hover:text-accent transition-colors text-sm font-medium">
              Pricing
            </a>
          </div>

          <div className="flex items-center gap-2">
            <Link to="/sign-in">
              <Button variant="ghost" size="sm" className="text-foreground">
                Sign In
              </Button>
            </Link>
            <Link to="/sign-up">
              <Button size="sm" className="bg-accent hover:bg-accent/90 text-accent-foreground shadow-sm">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section - Compact */}
      <section className="relative pt-24 pb-12 sm:pt-32 sm:pb-20" aria-labelledby="hero-heading">
        {/* Subtle background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />
        {/* Decorative elements */}
        <div className="absolute top-32 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl -translate-x-1/2" />
        <div className="absolute top-48 right-0 w-80 h-80 bg-primary/5 rounded-full blur-3xl translate-x-1/2" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left content */}
            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20 animate-fade-in-up animate-on-load" style={{ animationFillMode: 'forwards' }}>
                <Sparkles className="w-3.5 h-3.5 text-accent" />
                <span className="text-xs font-medium text-accent">AI-Powered Renovation Quotes</span>
              </div>

              {/* UI-005 FIX: Simplified headline - removed "renovation" per user feedback */}
              <h1 id="hero-heading" className="text-[2rem] sm:text-5xl lg:text-6xl font-bold text-foreground leading-[1.1] sm:leading-[1.05] tracking-tight animate-fade-in-up animate-on-load" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
                Turn photos into
                <span className="text-accent block"> paying clients</span>
              </h1>

              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-lg animate-fade-in-up animate-on-load" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
                Your clients snap a photo. Our AI generates stunning before/after visualizations with accurate quotes. Close deals faster.
              </p>

              {/* Sales-led CTA - Book a Call */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2 animate-fade-in-up animate-on-load" style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
                <a href="https://cal.com/renovationai/demo" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground h-12 px-7 shadow-lg shadow-accent/25 hover:shadow-xl hover:shadow-accent/30 transition-all hover:-translate-y-0.5">
                    Book a Demo Call
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </a>
              </div>

              {/* Early adopter message instead of fake stats */}
              <div className="flex items-center gap-3 pt-3 animate-fade-in-up animate-on-load" style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <span className="text-sm text-muted-foreground">
                  Join contractors transforming their sales process
                </span>
              </div>
            </div>

            {/* Right visual - Hero transformation card */}
            {/* UI-007 FIX: Increased hero image size for more visual impact */}
            <div className="relative animate-fade-in-left animate-on-load lg:scale-110 lg:translate-x-4" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
              {/* Main transformation card */}
              <div className="relative rounded-xl overflow-hidden shadow-2xl shadow-primary/10 border border-border/50 bg-card">
                <div className="aspect-[4/3] sm:aspect-[16/10] relative overflow-hidden">
                  <img
                    src="/images/hero-kitchen-transformation.png"
                    alt="Kitchen renovation before and after comparison"
                    className="absolute inset-0 w-full h-full object-cover"
                  />

                  {/* Before/After slider indicator */}
                  <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-1 bg-white/90 flex items-center justify-center z-10 shadow-sm">
                    <div className="w-10 h-10 rounded-full bg-white shadow-xl border-2 border-accent/30 flex items-center justify-center gap-0.5">
                      <ChevronRight className="w-3.5 h-3.5 text-accent rotate-180" />
                      <ChevronRight className="w-3.5 h-3.5 text-accent" />
                    </div>
                  </div>

                  {/* Labels */}
                  <div className="absolute top-3 left-3 px-2 py-1 rounded bg-white/90 backdrop-blur-sm text-xs font-medium text-foreground">
                    Before
                  </div>
                  <div className="absolute top-3 right-3 px-2 py-1 rounded bg-accent text-xs font-medium text-accent-foreground">
                    After (AI)
                  </div>
                </div>
              </div>

              {/* Floating quote card */}
              <div className="absolute -bottom-3 sm:-bottom-4 left-2 sm:left-4 bg-card rounded-lg p-2.5 sm:p-3 shadow-xl shadow-primary/10 border border-border/50 max-w-[160px] sm:max-w-[180px] animate-float-slow">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-accent" />
                  </div>
                  <div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Est. Quote</div>
                    <div className="text-lg font-bold text-foreground">$24,500</div>
                  </div>
                </div>
              </div>

              {/* Floating time badge */}
              <div className="absolute -top-1 sm:-top-2 right-2 sm:right-4 bg-card rounded-lg px-2.5 sm:px-3 py-1.5 sm:py-2 shadow-xl shadow-primary/10 border border-border/50 animate-float" style={{ animationDelay: '1s' }}>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-accent" />
                  <span className="text-xs font-medium text-foreground">8 sec generation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Compact */}
      <section id="features" className="py-16 sm:py-20 bg-muted/30" aria-labelledby="features-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 id="features-heading" className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
              Close more deals with AI-powered proposals
            </h2>
            <p className="text-muted-foreground">
              Stop losing leads to slow quotes. Show clients exactly what they'll get.
            </p>
          </div>

          {/* Feature Grid - single col on small mobile, 2x2 on mobile, 4 cols on desktop */}
          <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {[
              {
                icon: Sparkles,
                title: "AI Visualizations",
                description: "Transform photos into stunning renovation previews instantly",
              },
              {
                icon: Clock,
                title: "Instant Quotes",
                description: "Get accurate cost estimates in seconds, not days",
              },
              {
                icon: TrendingUp,
                title: "Higher Close Rate",
                description: "Win more deals by showing clients their dream space",
              },
              {
                icon: Shield,
                title: "Lead Verification",
                description: "AI verifies lead quality so you focus on ready clients",
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-card rounded-lg p-5 border border-border/50 hover:border-accent/40 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 group"
              >
                <div className="w-11 h-11 sm:w-10 sm:h-10 rounded-lg bg-accent/10 flex items-center justify-center mb-3 group-hover:bg-accent/15 transition-colors">
                  <feature.icon className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-semibold text-foreground text-sm sm:text-base mb-1.5">{feature.title}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Widget callout */}
          {/* UI-008 FIX: Removed confusing script tag display */}
          <div className="mt-8 bg-card rounded-xl p-5 border border-border/50 flex flex-col sm:flex-row items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="font-semibold text-foreground mb-1">Embeddable Widget</h3>
              <p className="text-sm text-muted-foreground">
                Add our widget to your website. Clients submit photos directly, and leads flow into your dashboard.
              </p>
            </div>
            <Link to="/sign-up" className="hidden sm:block">
              <Button variant="outline" size="sm" className="text-accent border-accent/30 hover:bg-accent/5">
                Get Widget Code
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works - Compact */}
      <section id="how-it-works" className="py-16 sm:py-20 bg-primary text-primary-foreground" aria-labelledby="how-it-works-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 id="how-it-works-heading" className="text-2xl sm:text-3xl font-bold mb-3">
              From photo to signed contract
            </h2>
            <p className="text-primary-foreground/70">
              Three simple steps to win more renovation projects
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 sm:gap-6">
            {[
              {
                step: "01",
                icon: Upload,
                title: "Client Submits Photo",
                description: "Your client uploads a photo of their space through your custom widget.",
              },
              {
                step: "02",
                icon: Image,
                title: "AI Generates Preview",
                description: "Our AI creates a stunning visualization with an accurate cost estimate.",
              },
              {
                step: "03",
                icon: FileCheck,
                title: "You Close the Deal",
                description: "Review the lead, refine if needed, and present a winning proposal.",
              },
            ].map((item, index) => (
              <div key={index} className="relative text-center">
                <div className="text-5xl font-bold text-primary-foreground/20 mb-2">{item.step}</div>
                <div className="w-14 h-14 rounded-xl bg-accent flex items-center justify-center mx-auto mb-4 shadow-lg shadow-accent/30">
                  <item.icon className="w-7 h-7 text-accent-foreground" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-primary-foreground/70 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Compact */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="relative rounded-2xl bg-gradient-to-br from-accent via-accent to-primary/80 p-8 sm:p-12 text-center overflow-hidden shadow-2xl shadow-accent/20">
            {/* Subtle pattern */}
            <div className="absolute inset-0 opacity-[0.07]" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1.5px, transparent 0)`,
              backgroundSize: '20px 20px',
            }} />
            {/* Decorative blur */}
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-primary/20 rounded-full blur-3xl" />

            <div className="relative">
              <h2 className="text-2xl sm:text-3xl font-bold text-accent-foreground mb-3">
                Ready to close more renovation deals?
              </h2>
              <p className="text-accent-foreground/80 mb-6 max-w-lg mx-auto">
                See how AI visualization can transform your sales process.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="https://cal.com/renovationai/demo" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto bg-white text-accent hover:bg-white/90 h-11 px-8 font-semibold shadow-lg">
                    Book a Demo Call
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </a>
                <Link to="/sign-in" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto border-2 border-white/30 bg-transparent text-accent-foreground hover:bg-white/10 h-11 px-8"
                  >
                    Sign In
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Minimal */}
      <footer className="py-8 sm:py-10 border-t border-border/40 bg-muted/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center gap-4 sm:gap-6 sm:flex-row sm:justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="font-semibold text-foreground">RenovationAI</span>
            </div>

            <div className="flex items-center gap-5 sm:gap-6 text-sm text-muted-foreground">
              <a href="#" className="hover:text-foreground transition-colors">Privacy</a>
              <a href="#" className="hover:text-foreground transition-colors">Terms</a>
              <a href="#" className="hover:text-foreground transition-colors">Contact</a>
            </div>

            <div className="text-xs sm:text-sm text-muted-foreground">
              © 2025 RenovationAI
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
